import { createSelector } from 'reselect';

import { BasePlan } from '~/types/resources';
import { AppState } from '~/store';

export const selectBasePlanId = (state: AppState) =>
  state.organizationResources.billing.subscription?.basePlanId;

export const selectSuccessorTireId = (state: AppState) =>
  state.organizationResources.billing.subscription?.successor?.tier.id;

export const selectBillingPlans = (state: AppState) => state.organizationResources.plans.byId ?? {};

export const selectCurrentPlanId = createSelector(
  selectSuccessorTireId,
  selectBasePlanId,
  (successorTireId, basePlanId) => successorTireId ?? basePlanId,
);

export const selectBasePlan = createSelector(
  selectBillingPlans,
  selectBasePlanId,
  (billingPlans, basePlanId) => {
    if (basePlanId) {
      return billingPlans[basePlanId] ?? ({} as BasePlan);
    }
    return {} as BasePlan;
  },
);

export const selectCurrentPlan = createSelector(
  selectBillingPlans,
  selectCurrentPlanId,
  (billingPlans, currentPlanId) => {
    if (currentPlanId) {
      return billingPlans[currentPlanId] ?? ({} as BasePlan);
    }
    return {} as BasePlan;
  },
);

/**
 * UI active plans are based on:
 * - active flag is set to true
 * - it isn't a partner plan
 * - it is paid or self service plan (not internal plan)
 */
export const selectActivePlans = createSelector(selectBillingPlans, (billingPlans) =>
  Object.values(billingPlans)
    .filter((plan) => plan.active && !plan.partnerPlan && (plan.paid || plan.selfService))
    .sort((planA, planB) => planA.level - planB.level),
);

export const selectActiveSelfServicePlans = createSelector(
  selectActivePlans,
  (activeBillingPlans) => activeBillingPlans.filter((plan) => plan.selfService),
);

/**
 * Enhanced usage with fallback values
 */
export const selectUsage = createSelector(
  [
    (state: AppState) => state.organizationResources.usage ?? {},
    (state: AppState) => state.organizationResources.billing.subscription,
  ],
  (usage, subscription) => {
    const allowOnDemandMinutes = !!usage.allowOnDemandMinutes;
    const onDemandMinutesLimit = usage.onDemandMinutesLimit ?? 0;
    const onDemandMinutes = usage.onDemandMinutes ?? 0;
    const onDemandRunMinutePrice = subscription?.basePlan?.onDemandRunMinutePrice ?? 0;
    const quotaRunMinutesPerPeriod = usage.quotaRunMinutesPerPeriod ?? 0;
    const usedQuotaRunMinutes = usage.usedQuotaRunMinutes ?? 0;

    const maxOnDemandRunMinutes = subscription?.basePlan?.limits.maxOnDemandRunMinutes;
    const canEnableOnDemandRunMinutes =
      typeof maxOnDemandRunMinutes === 'number'
        ? maxOnDemandRunMinutes > 0
        : maxOnDemandRunMinutes === null;

    const allowOnDemandAssistantRuns = !!usage.allowOnDemandAssistantRuns;
    const onDemandAssistantRunsLimit = usage.onDemandAssistantRunsLimit ?? 0;
    const onDemandAssistantRuns = usage.onDemandAssistantRuns ?? 0;
    const onDemandAssistantRunPrice = subscription?.basePlan?.onDemandAssistantRunPrice ?? 0;
    const quotaAssistantRunsPerPeriod = usage.quotaAssistantRunsPerPeriod ?? 0;
    const usedQuotaAssistantRuns = usage.usedQuotaAssistantRuns ?? 0;
    const usedQuotaAssistantRunsPercentage =
      usedQuotaAssistantRuns / quotaAssistantRunsPerPeriod || 0; // possible NaN || 0 => 0
    const usedOnDemandAssistantRunsPercentage =
      onDemandAssistantRuns / onDemandAssistantRunsLimit || 0; // possible NaN || 0 => 0

    const maxOnDemandAssistantRuns = subscription?.basePlan?.limits.maxOnDemandAssistantRuns;
    const canEnableOnDemandAssistantRuns =
      typeof maxOnDemandAssistantRuns === 'number'
        ? maxOnDemandAssistantRuns > 0
        : maxOnDemandAssistantRuns === null;

    return {
      ...usage,
      allowOnDemandMinutes,
      onDemandMinutesLimit,
      onDemandMinutes,
      onDemandRunMinutePrice,
      quotaRunMinutesPerPeriod,
      usedQuotaRunMinutes,
      usedQuotaRunMinutesPercentage: usedQuotaRunMinutes / quotaRunMinutesPerPeriod || 0, // possible NaN || 0 => 0
      usedOnDemandRunMinutesPercentage: onDemandMinutes / onDemandMinutesLimit || 0, // possible NaN || 0 => 0
      maxOnDemandRunMinuteExpenditure: onDemandMinutesLimit * onDemandRunMinutePrice || 0, // possible NaN || 0 => 0
      canEnableOnDemandRunMinutes: canEnableOnDemandRunMinutes && onDemandRunMinutePrice > 0,
      allowOnDemandAssistantRuns,
      onDemandAssistantRunsLimit,
      onDemandAssistantRuns,
      onDemandAssistantRunPrice,
      quotaAssistantRunsPerPeriod,
      usedQuotaAssistantRuns,
      usedQuotaAssistantRunsPercentage,
      usedOnDemandAssistantRunsPercentage,
      maxOnDemandAssistantRunExpenditure:
        onDemandAssistantRunsLimit * onDemandAssistantRunPrice || 0, // possible NaN || 0 => 0
      canEnableOnDemandAssistantRuns:
        canEnableOnDemandAssistantRuns && onDemandAssistantRunPrice > 0,
      unlimitedRunMinutesEnabled: usage.unlimitedRunMinutesEnabled ?? false,
      /**
       * Might be useful to save this (overage) in organization.
       * - run minutes overage are determined like: organization?.rmin_state === 'overflow'
       */
      totalAssistantRunUsageExceedsLimits: allowOnDemandAssistantRuns
        ? usedOnDemandAssistantRunsPercentage > 1
        : usedQuotaAssistantRunsPercentage > 1,
    };
  },
);
